import React, { useState, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import * as nsfwjs from "nsfwjs";
import * as tf from "@tensorflow/tfjs";
import { useEffect } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../firebase";

const EditPostForm = ({ post, onSave, onCancel }) => {
  const [title, setTitle] = useState(post.title || "");
  const [description, setDescription] = useState(post.description || "");
  const [media, setMedia] = useState(
    [
      ...(post.images?.map((url) => ({ url, type: "image" })) || []),
      ...(post.videos?.map((url) => ({ url, type: "video" })) || []),
    ] || []
  );
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [objetivo, setObjetivo] = useState(post.objetivo || "");
  const [categoria, setCategoria] = useState(post.categoria || "");
  const [fechaInicio, setFechaInicio] = useState(post.fechaInicio || "");
  const [fechaFin, setFechaFin] = useState(post.fechaFin || "");
  const [ctaTexto, setCtaTexto] = useState(post.ctaTexto || "");
  const [ctaLink, setCtaLink] = useState(post.ctaLink || "");
  const [descuento, setDescuento] = useState(post.descuento || "");
  const [destacado, setDestacado] = useState(post.destacado || false);
  const [visibilidad, setVisibilidad] = useState(
    typeof post.visibilidad === "boolean" ? post.visibilidad : true
  );
  const modelRef = useRef(null);
  const [loadingNSFW, setLoadingNSFW] = useState(false);

  const maxMedia = 5;
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadModel = async () => {
      modelRef.current = await nsfwjs.load();
    };
    loadModel();
  }, []);

  // NSFW solo para imágenes, con timeout
const isFileNSFW = (file) => {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image") || !modelRef.current) return resolve(false);

    const mediaElement = document.createElement("img");
    const url = URL.createObjectURL(file);
    mediaElement.src = url;

    const timeout = setTimeout(() => {
      URL.revokeObjectURL(url);
      resolve(false); // si tarda demasiado, asumimos que está bien
    }, 15000); // 15 segundos máximo

    mediaElement.onload = async () => {
      clearTimeout(timeout);
      try {
        const predictions = await modelRef.current.classify(mediaElement);
        const nsfw = predictions.find(
          (p) => ["Porn", "Hentai", "Sexy"].includes(p.className) && p.probability > 0.7
        );
        URL.revokeObjectURL(url);
        resolve(!!nsfw);
      } catch (err) {
        console.error("Error clasificando NSFW:", err);
        URL.revokeObjectURL(url);
        resolve(false);
      }
    };

    mediaElement.onerror = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      resolve(false);
    };
  });
};


  const uploadFile = async (file) => {
  if (!file) return null;

  try {
    setUploading(true);

    // Crear ruta única en Firebase Storage
    const filePath = `PromotionalPosts/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);

    // Subir archivo
    const snapshot = await uploadBytes(storageRef, file);

    // Obtener URL pública
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (err) {
    console.error("Error subiendo archivo a Firebase:", err);
    setError("Error subiendo archivo");
    setShowAlert(true);
    return null;
  } finally {
    setUploading(false);
  }
};


  // Manejo de subida optimizado
const handleFileChange = async (e) => {
  const files = Array.from(e.target.files);
  if (!files.length) return;

  if (media.length + files.length > maxMedia) {
    setError(`Solo puedes subir hasta ${maxMedia} archivos.`);
    setShowAlert(true);
    return;
  }

  setError(null);
  setUploading(true);

  const uploadedMedia = [];

  for (let file of files) {
    // NSFW solo para imágenes
    if (file.type.startsWith("image")) {
      setLoadingNSFW(true);
      const nsfw = await isFileNSFW(file);
      setLoadingNSFW(false);

      if (nsfw) {
        Swal.fire(
          "Contenido bloqueado",
          "Este archivo contiene contenido inapropiado (NSFW).",
          "error"
        );
        continue; // saltar archivo
      }
    }

    // Subir archivo a Firebase
    const url = await uploadFile(file);
    if (url) {
      uploadedMedia.push({
        url,
        type: file.type.startsWith("video/") ? "video" : "image",
      });
    }
  }

  if (uploadedMedia.length > 0) {
    setMedia((prev) => [...prev, ...uploadedMedia]);
  }

  e.target.value = null;
  setUploading(false);
};



  const removeMedia = (url) => {
    setMedia(media.filter((item) => item.url !== url));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!title.trim()) {
      setError("El título es obligatorio");
      setShowAlert(true);
      return;
    }

    if (media.length === 0) {
      setError("Debes subir al menos una imagen o video");
      setShowAlert(true);
      return;
    }

    setError(null);

    onSave({
      ...post,
      title,
      description,
      images: media.filter((m) => m.type === "image").map((m) => m.url),
      videos: media.filter((m) => m.type === "video").map((m) => m.url),
      objetivo,
      categoria,
      fechaInicio,
      fechaFin,
      ctaTexto,
      ctaLink,
      descuento,
      destacado,
      visibilidad,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-900 rounded-lg shadow-lg space-y-6 transition-colors duration-300"
    >
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 text-center">
        Editar Publicación Promocional
      </h2>

      {/* Título y Descripción */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="title"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Título *
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
            required
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
            placeholder="Título de la publicación"
          />
        </div>

        <div>
          <label
            htmlFor="description"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Descripción
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
            placeholder="Describe tu promoción, producto o evento"
          />
        </div>
      </div>

      {/* Upload Files */}
      <div>
        <label className="block mb-2 text-gray-700 dark:text-gray-300 font-semibold">
          Subir imágenes/videos (máx {maxMedia})
        </label>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || media.length >= maxMedia}
          className={`inline-flex items-center gap-2 px-5 py-3 rounded-md font-semibold text-white transition 
            ${
              uploading || media.length >= maxMedia
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800"
            } focus:outline-none focus:ring-4 focus:ring-blue-400 dark:focus:ring-blue-700`}
          aria-label="Seleccionar archivos multimedia"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-6 w-6 ${uploading ? "animate-spin" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v16h16" />
          </svg>
          {uploading ? "Subiendo..." : "Seleccionar archivos"}
        </button>
        <input
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileChange}
          disabled={uploading || media.length >= maxMedia}
          ref={fileInputRef}
          className="hidden"
        />
      </div>

      {/* Previsualización multimedia */}
      <div className="flex flex-wrap gap-4 mt-4">
        {media.map(({ url, type }) => (
          <div
            key={url}
            className="relative w-28 h-28 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700 shadow-lg group"
          >
            {type === "image" ? (
              <img
                src={url}
                alt="Preview"
                className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
            ) : (
              <video
                src={url}
                controls
                className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
              />
            )}
            <button
              type="button"
              onClick={() => removeMedia(url)}
              className="absolute top-1 right-1 bg-red-600 text-white rounded-full px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg hover:bg-red-700"
              aria-label="Eliminar archivo"
              title="Eliminar"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Otros campos: objetivo, categoria, fechas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label
            htmlFor="objetivo"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Objetivo
          </label>
          <select
            id="objetivo"
            value={objetivo}
            onChange={(e) => setObjetivo(e.target.value)}
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
          >
            <option value="">Selecciona un objetivo</option>
            <option>Generar ventas</option>
            <option>Promocionar producto</option>
            <option>Reforzar marca</option>
            <option>Conseguir seguidores</option>
            <option>Promocionar evento</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="categoria"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Categoría
          </label>
          <select
            id="categoria"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
          >
            <option value="">Selecciona una categoría</option>
            <option>Productos</option>
            <option>Eventos</option>
            <option>Servicios</option>
            <option>Marcas</option>
            <option>Otros</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="fechaInicio"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Fecha Inicio
          </label>
          <input
            type="date"
            id="fechaInicio"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
          />
        </div>

        <div>
          <label
            htmlFor="fechaFin"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Fecha Fin
          </label>
          <input
            type="date"
            id="fechaFin"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            min={fechaInicio}
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
          />
        </div>
      </div>

      {/* CTA, Descuento */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="ctaTexto"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Texto del CTA
          </label>
          <input
            type="text"
            id="ctaTexto"
            value={ctaTexto}
            onChange={(e) => setCtaTexto(e.target.value)}
            maxLength={50}
            placeholder="Ej. Comprar ahora"
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
          />
        </div>

        <div>
          <label
            htmlFor="ctaLink"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            URL del CTA
          </label>
          <input
            type="url"
            id="ctaLink"
            value={ctaLink}
            onChange={(e) => setCtaLink(e.target.value)}
            placeholder="https://ejemplo.com"
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="descuento"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Descuento (%)
          </label>
          <input
            type="number"
            id="descuento"
            value={descuento}
            onChange={(e) =>
              setDescuento(e.target.value.replace(/[^0-9]/g, ""))
            }
            min={0}
            max={100}
            placeholder="Ej. 15"
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
          />
        </div>

        <div className="flex items-center space-x-4 mt-7">
          <label
            htmlFor="destacado"
            className="text-gray-700 dark:text-gray-300 font-semibold"
          >
            Destacado
          </label>
          <input
            type="checkbox"
            id="destacado"
            checked={destacado}
            onChange={() => setDestacado(!destacado)}
            className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Visibilidad */}
      <div className="flex items-center space-x-4">
        <label
          htmlFor="visibilidad"
          className="text-gray-700 dark:text-gray-300 font-semibold"
        >
          Visible
        </label>
        <input
          type="checkbox"
          id="visibilidad"
          checked={visibilidad}
          onChange={() => setVisibilidad(!visibilidad)}
          className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
        />
      </div>


      {/* Botones */}
      <div className="flex justify-end space-x-4 mt-6">
        <button
          type="submit"
          disabled={uploading}
          className={`bg-indigo-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-indigo-700 transition disabled:opacity-60 disabled:cursor-not-allowed`}
        >
          Guardar
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-200 px-6 py-3 rounded-md font-semibold hover:bg-gray-400 dark:hover:bg-gray-600 transition"
        >
          Cancelar
        </button>
      </div>
      {showAlert && <AlertModal message={error} onClose={() => setShowAlert(false)} />}

    </form>
  );
};

const AlertModal = ({ message, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white dark:bg-gray-900 p-6 rounded shadow-lg max-w-sm w-full">
      <p className="text-red-600 dark:text-red-400 font-semibold mb-4">{message}</p>
      <button
        onClick={onClose}
        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition"
      >
        Cerrar
      </button>
    </div>
  </div>
);

export default EditPostForm;
